import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { LicensesUtilization } from '../../types/licenses-utilization.type';
import { DateHelper } from '../../helpers/date.helper';
import { BucketedUtilization } from '../../types/bucketed-utilization.type';
import { ResourceList } from '../../types/resource-list.type';
import { ResourcesUtilizationFilter } from '../../types/resources-utilization-filter.type';
import { ResourcesUtilization } from '../../types/resources-utilization.type';
import { RunningProcessLicensesInfo } from '../../types/running-process-licenses-info.type';
import { RunningProcessResourcesInfo } from '../../types/running-process-resources-info.type';
import { Schedule } from '../../types/schedule.type';

@Injectable({ providedIn: 'root' })
export class UtilizationApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/utilization`;
  }

  async getLicensesUtilization(tenantId: string, date: Date, useOrchestrationLicenseCount: boolean): Promise<LicensesUtilization> {
    const result = await this.get<LicensesUtilization>(`licenses/${tenantId}/${DateHelper.formatApiDateOnly(date)}/${useOrchestrationLicenseCount === true}`);
    ApiConversionHelper.convertUtilization(result);
    return result;
  }

  async getBucketedLicensesUtilization(tenantId: string, from: Date, to: Date, useOrchestrationLicenseCount: boolean): Promise<BucketedUtilization> {
    const result = await this.get<BucketedUtilization>(`licenses-bucketed/${tenantId}/${DateHelper.formatApiDateOnly(from)}/${DateHelper.formatApiDateOnly(to)}/${useOrchestrationLicenseCount === true}`);
    ApiConversionHelper.convertBucketedUtilization(result);
    return result;
  }

  async getResources(tenantId: string, from: Date, to: Date): Promise<ResourceList> {
    return await this.get<ResourceList>(`resources/list/${tenantId}/${DateHelper.formatApiDateOnly(from)}/${DateHelper.formatApiDateOnly(to)}`);
  }

  async getResourcesUtilization(tenantId: string, from: Date, to: Date, filter: ResourcesUtilizationFilter): Promise<ResourcesUtilization> {
    const result = await this.postWithResult<any, ResourcesUtilization>(`resources/${tenantId}/${DateHelper.formatApiDateOnly(from)}/${DateHelper.formatApiDateOnly(to)}`, { ...filter }, null, true);
    ApiConversionHelper.convertUtilization(result);
    return result;
  }

  async getBucketedResourcesUtilization(tenantId: string, from: Date, to: Date, filter: ResourcesUtilizationFilter): Promise<BucketedUtilization> {
    const result = await this.postWithResult<any, BucketedUtilization>(`resources-bucketed/${tenantId}/${DateHelper.formatApiDateOnly(from)}/${DateHelper.formatApiDateOnly(to)}`, { ...filter }, null, true);
    ApiConversionHelper.convertBucketedUtilization(result);
    return result;
  }

  async getRunningProcessesLicensesInfo(tenantId: string, timeStamp: Date): Promise<RunningProcessLicensesInfo[]> {
    return await this.get<RunningProcessLicensesInfo[]>(`licenses/processes/${tenantId}/${DateHelper.formatApiDateTime(timeStamp)}`);
  }

  async getRunningProcessesResourcesInfo(tenantId: string, timeStamp: Date, filter: ResourcesUtilizationFilter): Promise<RunningProcessResourcesInfo[]> {
    return await this.postWithResult<any, RunningProcessResourcesInfo[]>(`resources/processes/${tenantId}/${DateHelper.formatApiDateTime(timeStamp)}`, filter, null, true);
  }

  async getProcessScheduleResources(tenantId: string, date: Date): Promise<Schedule> {
    const result = await this.get<Schedule>(`resources/schedule/resources/${tenantId}/${DateHelper.formatApiDateOnly(date)}`);
    ApiConversionHelper.convertSchedule(result);
    return result;
  }

  async getProcessScheduleUsers(tenantId: string, date: Date): Promise<Schedule> {
    const result = await this.get<Schedule>(`resources/schedule/users/${tenantId}/${DateHelper.formatApiDateOnly(date)}`);
    ApiConversionHelper.convertSchedule(result);
    return result;
  }

  async getLicenseScheduleUtilization(tenantId: string, date: Date): Promise<Schedule> {
    const result = await this.get<Schedule>(`licenses/schedule/utilization/${tenantId}/${DateHelper.formatApiDateOnly(date)}`);
    ApiConversionHelper.convertSchedule(result);
    return result;
  }
}
